/* eslint-disable no-param-reassign */
import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import { RegionType } from '@src/slide/domain/selection/constant/region';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import CommonTips, { CommonTipsType } from '@com/common-tips';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import _unset from 'lodash/unset';
import i18n from '@i18next/i18n';
import _has from 'lodash/has';
import _get from 'lodash/get';
import { DataType } from '@tencent/slide-sdk-model/src/data';
import { VER2_3_TABLE_ATTR_PATH } from '@src/slide/domain/ui-model/converter/node-property/element/table-property-converter';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { ITextBody } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';

export const htmlToSdkScale = 12700; // html 中转为sdk的比例
export const htmlToSdkFontScale = 100;

export const EventType = {
    MouseEvent: 0, // 鼠标点击
    ClipboardEvent: 1, // 浏览器事件 如copy、浏览器菜单栏触发
    KeyboardEvent: 2, // 键盘事件
    DragEvent: 3,
} as const;
export type EventTypeValue = typeof EventType[keyof typeof EventType];

export class ServicesProvider {
    public static selectionService: ISelectionService;
    public static uiDataService: IUiDataService;

    public static setServicesDelegate(services: {
        selectionService?: ISelectionService;
        uiDataService?: IUiDataService;
    }) {
        this.selectionService = this.selectionService ?? services.selectionService;
        this.uiDataService = this.uiDataService ?? services.uiDataService;
    }
}

export class SlideDataAdapter {
    public static checkStateForLayout(): boolean {
        return ServicesProvider.selectionService?.slideClearanceIndex === 0
            || (ServicesProvider.selectionService?.slideClearanceIndex === -1
                && ServicesProvider.selectionService?.currentSlideIndex === -1);
    }

    public static getLayout = () => {
        let layout;
        if (SlideDataAdapter.checkStateForLayout()) {
            [layout] = ServicesProvider.uiDataService.presentation.masterList[0].layoutList;
        } else {
            try {
                const curSlide = ServicesProvider.selectionService?.currentSlide;
                let masterIndex = -1;
                const { masterList } = ServicesProvider.uiDataService.presentation;
                for (let i = 0; i < masterList.length; i++) {
                    if (curSlide?.sdkData?.getMaster()?.id === masterList[i].id) {
                        masterIndex = i;
                        break;
                    }
                }
                if (masterIndex === -1) {
                    masterIndex = 0;
                }
                let layoutIndex = -1;
                const { layoutList } = ServicesProvider.uiDataService.presentation.masterList[masterIndex];
                for (let i = 0; i < layoutList.length; i++) {
                    if ((curSlide?.sdkData as any)?.layoutId === layoutList[i].id) {
                        layoutIndex = i;
                        break;
                    }
                }
                if (layoutIndex === -1) {
                    layoutIndex = 0;
                }
                layout = ServicesProvider.uiDataService.presentation.masterList[masterIndex].layoutList[layoutIndex];
            } catch (error) {
                if (ServicesProvider.uiDataService.presentation.masterList.length > 0) {
                    [layout] = ServicesProvider.uiDataService.presentation.masterList[0].layoutList;
                } else {
                    layout = { id: 0 };
                }
            }
        }
        return layout;
    };

    public static checkPlaceHolder = (slideSDKData) => {
        // 处理placeHolder
        const unsetPlaceHolder = (models) => {
            models.forEach((model) => {
                if (model.type === 'shape' && model.isPlaceholder) {
                    _unset(model.props, 'placeholder');
                }
            });
        };
        const sd = slideSDKData.slideData;
        if (_get(sd, 'groupShape.children.length') > 0) {
            unsetPlaceHolder(sd.groupShape.children);
        }
    };
    // 缩放文本框内部
    public static adaptTextBody = (textBody: ITextBody, ratio: number) => {
        if (_has(textBody, 'paragraphList')) {
            textBody.paragraphList!.forEach((paragraph: any) => {
                if (_get(paragraph, 'paragraphItem.length')) {
                    paragraph.paragraphItem.forEach((paragraphItem: any) => {
                        if (paragraphItem.props?.font_size) {
                            // 处理文字大小
                            paragraphItem.props.font_size = paragraphItem.props.font_size * ratio;
                        }
                    });

                    // 段落缩进相关
                    if (_has(paragraph, 'paragraphProps')) {
                        if (_has(paragraph.paragraphProps, 'indent')) {
                            paragraph.paragraphProps.indent = paragraph.paragraphProps.indent * ratio;
                        }
                        if (_has(paragraph.paragraphProps, 'marginLeft')) {
                            paragraph.paragraphProps.marginLeft = paragraph.paragraphProps.marginLeft * ratio;
                        }
                        if (_has(paragraph, 'paragraphProps.lnSpc.spcPts')) {
                            const props = paragraph.paragraphProps;
                            props.lnSpc.spcPts = props.lnSpc.spcPts * ratio;
                        }
                        if (_has(paragraph, 'paragraphProps.spcAft.spcPts')) {
                            const props = paragraph.paragraphProps;
                            props.spcAft.spcPts = props.spcAft.spcPts * ratio;
                        }
                        if (_has(paragraph, 'paragraphProps.spcBef.spcPts')) {
                            const props = paragraph.paragraphProps;
                            props.spcBef.spcPts = props.spcBef.spcPts * ratio;
                        }
                    }
                }
                if (_get(paragraph, 'endParaRPr.font_size')) {
                    paragraph.endParaRPr.font_size = paragraph.endParaRPr.font_size * ratio;
                }
            });
        }

        if (_has(textBody, 'textStyleList')) {
            (textBody as any).textStyleList.forEach((textStyle: any) => {
                textStyle.defaultRunProps.font_size = textStyle.defaultRunProps.font_size * ratio;
            });
        }

        // 文本框缩进相关
        SlideDataAdapter.lineIndent(textBody, ratio);
    };

    public static lineIndent = (textBody, ratio: number) => {
        if (_has(textBody, 'props')) {
            if (_has(textBody.props, 'autoFitType')) {
                _unset(textBody.props, 'autoFitType');
                if (_has(textBody.props, 'bottom_inset')) {
                    textBody.props.bottom_inset = textBody.props.bottom_inset * ratio;
                }
                if (_has(textBody.props, 'left_inset')) {
                    textBody.props.left_inset = textBody.props.left_inset * ratio;
                }
                if (_has(textBody.props, 'right_inset')) {
                    textBody.props.right_inset = textBody.props.right_inset * ratio;
                }
                if (_has(textBody.props, 'top_inset')) {
                    textBody.props.top_inset = textBody.props.top_inset * ratio;
                }
            }
        }
    };

    // 缩放组内部的文字
    public static adaptInnerGroupShape = (groupShape, ratio: number) => {
        if (groupShape.shapeList?.length) {
            groupShape.shapeList.forEach((element) => {
                if (element.type === 'groupShape') {
                    // 递归处理成组
                    SlideDataAdapter.adaptInnerGroupShape(element, ratio);
                }
                if (element.textBody?.paragraphList) {
                    SlideDataAdapter.adaptTextBody(element.textBody, ratio);
                }
            });
        }
    };

    // 缩放table及内部元素
    public static adaptTable = (tableData: any, ratio: number, ratioH: number) => {
        if (tableData.graphicData?.tbl) {
            tableData.graphicData.tbl.tblColList.forEach((tblCol) => {
                tblCol.w = tblCol.w * ratio;
            });

            tableData.graphicData.tbl.tblRowList.forEach((tblRow) => {
                tblRow.h = tblRow.h * ratioH;

                if (tblRow.tcList?.length) {
                    tblRow.tcList.forEach((tc) => {
                        if (tc.textBody) {
                            SlideDataAdapter.adaptTextBody(tc.textBody, ratio);
                        }
                    });
                }
            });
        }
    };

    public static adaptElement = (element: any, ratio: number, ratioH: number) => {
        if (element.type === 'groupShape') {
            SlideDataAdapter.adaptInnerGroupShape(element, ratio);
        }

        if (element.type === 'shape' && element.textBody && element.textBody.paragraphList) {
            SlideDataAdapter.adaptTextBody(element.textBody, ratio);
        }

        if (element.type === 'graphFrame_Tbl') {
            SlideDataAdapter.adaptTable(element, ratio, ratioH);
        }
    };
}

declare global {
    interface Window {
        clipboardData: DataTransfer;
    }
}
export default class CPUtils {
    /**
     * 获取剪切板Data
     *
     * @static
     * @param {*} event
     * @returns
     * @memberof CPUtils
     */
    public static getClipboardData(event): DataTransfer {
        const oriEvent = event.originalEvent;
        return event.clipboardData || (oriEvent?.clipboardData) || window.clipboardData;
    }

    /**
     * 是否什么都没选中
     * @static
     * @returns
     * @memberof CPUtils
     */
    public static isSelectedNone(): boolean {
        const slides = ServicesProvider.selectionService?.selectedSlides?.selectedArray;
        const elements = ServicesProvider.selectionService?.selectedElements?.selectedArray;
        const selectedNoteRegion = (ServicesProvider.selectionService?.lastMouseDownRegion === RegionType.NOTE);
        if ((!slides?.length) && (!elements?.length) && !selectedNoteRegion) {
            return true;
        }
        return false;
    }

    /**
     * 剪切的删除元素部分
     *
     * @static
     * @memberof CPUtils
     */
    public static doCutDelete(): void {
        switch (ServicesProvider.selectionService?.lastMouseDownRegion) {
            case RegionType.SLIDELIST:
                ServicesProvider.uiDataService.presentation.deleteSlide();
                break;
            case RegionType.EDITOR:
                {
                    let shouldDeleteElement = true;
                    const elements = ServicesProvider.selectionService?.selectedElements;
                    const selectedSlideElements = elements.selectedArrayForType(SelectedEleMgrType.SLIDE);
                    // table
                    if (selectedSlideElements.length === 1 && selectedSlideElements[0].type === UIModelType.TABLE) {
                        const autoFocusAfterClear = false;
                        // eslint-disable-next-line prefer-destructuring
                        const curElement = selectedSlideElements[0] as ITableModel;
                        const cleared = curElement.clearSelectedCellContent(autoFocusAfterClear);
                        // 判断是否单元格全部cut，全部cut的话 删除表格
                        const selectedCellCount = curElement.selectedCellRanges.length;
                        let cellCount = 0;
                        curElement.props.items.forEach((row) => {
                            cellCount += row.filter(cell => !cell.vMerge && !cell.hMerge).length;
                        });
                        const allCellSelected = selectedCellCount === cellCount;
                        if (cleared === true && !allCellSelected) {
                            shouldDeleteElement = false;
                        }
                    }
                    if (shouldDeleteElement) {
                        ServicesProvider.selectionService?.currentSlide?.deleteElement();
                    }
                }
                break;
        }
    }

    /**
     *防止tips重复显示的闭包
     *
     * @static
     * @returns
     * @memberof CPUtils
     */
    public static tableCannotPasteTipFun(): (tipConf) => void {
        const duration = 2000;
        const commonTipsConfig = {
            message: i18n.t('表格暂不支持此格式粘贴'),
            commonTipsType: CommonTipsType.INFO,
            block: false,
            autoClose: true,
            duration,
        };
        let isSnackbarShow = false;
        return (tipConf) => {
            if (!isSnackbarShow) {
                // snackbar组件 autoClose = true 时，返回值是 undefined
                // 所以这里使用中间变量 isSnackbarShow
                CommonTips.getInstance().show({ ...commonTipsConfig, ...tipConf });
                setTimeout(() => {
                    isSnackbarShow = false;
                }, duration);
                isSnackbarShow = true;
            }
        };
    }

    /**
     * 获得事件类型
     *
     * @static
     * @memberof CPUtils
     */
    public static getEventType = (event: any): EventTypeValue => {
        let type: EventTypeValue = EventType.MouseEvent;
        if (event) {
            const object = Object.prototype.toString.call(event); // '[object xxxx]'
            type = CPUtils.getEventTypeWithTypeName(object, type);
        }
        return type;
    };

    public static getEventTypeWithTypeName(object: string, type: EventTypeValue): EventTypeValue {
        if (object.length > 8) {
            if (object.indexOf('KeyboardEvent') > 0) {
                type = EventType.KeyboardEvent;
            }
            if (object.indexOf('ClipboardEvent') > 0) {
                type = EventType.ClipboardEvent;
            }
            if (object.indexOf('DragEvent') > 0) {
                type = EventType.DragEvent;
            }
            if (object.indexOf('MouseEvent') > 0) {
                type = EventType.MouseEvent;
            }
        }
        return type;
    }

    public static isCellInSelection(x: number, y: number, selection: any[]): boolean {
        let inSelection = false;
        for (const {
            rowStart, rowEnd, colStart, colEnd,
        } of selection) {
            if (x >= rowStart && x <= rowEnd && y >= colStart && y <= colEnd) {
                inSelection = true;
                break;
            }
        }
        return inSelection;
    }

    /**
     * 提取选中部分数据
     *
     * @static
     * @memberof CPUtils
     */
    public static extractTblRangeData = (sdkData) => {
        const { selectedCellRanges } = sdkData;

        if (!selectedCellRanges) {
            return;
        }

        const tblColList = _get(sdkData, VER2_3_TABLE_ATTR_PATH.tblColList);
        const tblRowList = _get(sdkData, VER2_3_TABLE_ATTR_PATH.tblRowList);
        const tcListPath = VER2_3_TABLE_ATTR_PATH.tcList;
        const textBodyPath = VER2_3_TABLE_ATTR_PATH.textBody;

        // 有合并的格 或者 选区不对称，需要补格处理
        // 计算生成的矩形选区范围
        const rowStartMin = Math.min(...selectedCellRanges.map(range => range.rowStart));
        const rowEndMax = Math.max(...selectedCellRanges.map(range => range.rowEnd));
        const colStartMin = Math.min(...selectedCellRanges.map(range => range.colStart));
        const colEndMax = Math.max(...selectedCellRanges.map(range => range.colEnd));

        // emit data
        tblColList.splice(colEndMax + 1, tblColList.length - colEndMax);
        tblColList.splice(0, colStartMin);
        tblRowList.splice(rowEndMax + 1, tblRowList.length - rowEndMax);
        tblRowList.splice(0, rowStartMin);
        tblRowList.forEach((row, rowIndex) => {
            row[tcListPath].splice(colEndMax + 1, row[tcListPath].length - colEndMax);
            row[tcListPath].splice(0, colStartMin);

            rowIndex += rowStartMin;

            row[tcListPath].forEach((cell, cellIndex) => {
                cellIndex += colStartMin;
                CPUtils.extractTblRangeDataOfCell(
                    rowIndex, cellIndex, selectedCellRanges,
                    cell, rowEndMax, colEndMax, colStartMin, rowStartMin,
                );
            });
        });

        // 仅选中一个单元格 (合并后的也算)，按照文本处理
        let selectedCellCount = 0;
        tblRowList.forEach((row) => {
            selectedCellCount += row[tcListPath].filter(cell => !cell.vMerge && !cell.hMerge).length;
        });
        if (selectedCellCount === 1) {
            // eslint-disable-next-line no-undef
            sdkData.type = DataType.Shape;
            sdkData[textBodyPath] = tblRowList[0][tcListPath][0][textBodyPath];
            sdkData.nvPr.txBox = true;
        }
    };

    public static extractTblRangeDataOfCell(
        rowIndex: number, cellIndex: number, selectedCellRanges: any[],
        cell: any, rowEndMax: number, colEndMax: number, colStartMin: number, rowStartMin: number,
    ): void {
        // 补格
        if (!CPUtils.isCellInSelection(rowIndex, cellIndex, selectedCellRanges)) {
            cell[VER2_3_TABLE_ATTR_PATH.textBody] = {
                [VER2_3_TABLE_ATTR_PATH.paragraphList]: [],
            };
            delete cell.rowSpan;
            delete cell.gridSpan;
            delete cell.hMerge;
            delete cell.vMerge;
        }

        // rowSpan / gridSpan 处理，减去截断部分
        if (CPUtils.extractTblRangeDataOfCellCheckRow(cell, rowIndex, rowEndMax)) {
            cell.rowSpan -= rowIndex + cell.rowSpan - rowEndMax - 1;
        }
        if (CPUtils.extractTblRangeDataOfCellCheckCol(cell, cellIndex, colEndMax)) {
            cell.gridSpan -= cellIndex + cell.gridSpan - colEndMax - 1;
        }

        // 处理边缘格的 hMerge/vMerge, 使生成的表格 border 正常
        if (cellIndex === colStartMin) {
            delete cell.hMerge;
        }
        if (rowIndex === rowStartMin) {
            delete cell.vMerge;
        }
    }

    public static extractTblRangeDataOfCellCheckCol(cell: any, cellIndex: number, colEndMax: number): boolean {
        return cell.gridSpan && cell.gridSpan > 1 && cellIndex + cell.gridSpan >= colEndMax + 1;
    }

    public static extractTblRangeDataOfCellCheckRow(cell: any, rowIndex: number, rowEndMax: number): boolean {
        return cell.rowSpan && cell.rowSpan > 1 && rowIndex + cell.rowSpan >= rowEndMax + 1;
    }

    /**
     * 选中最后添加的元素
     * @param {number} count 元素个数
     */
    public static selectLastAddElement = (count: number) => {
        if (count) {
            if (ServicesProvider.selectionService?.currentSlide
                && ServicesProvider.selectionService?.currentSlide.children
                && ServicesProvider.selectionService?.currentSlide.children.length > 0) {
                const { children } = ServicesProvider.selectionService?.currentSlide ?? {};
                const addedElement = children.slice(children.length - count, children.length);
                ServicesProvider.selectionService?.selectedElements.clear();
                ServicesProvider.selectionService?.selectedElements.add(...addedElement);
            }
        }
    };

    /**
     * 选中最后添加的slide
     * @param {number} count 元素个数
     */
    public static selectLastAddSlide(index: number, count: number, addType: UIModelType = UIModelType.SLIDE): void {
        ServicesProvider.selectionService.updateCurrentSlideType(addType);
        const addedSlide = ServicesProvider.selectionService?.slides.slice(index, index + count);
        ServicesProvider.selectionService?.selectedSlides.clear();
        if (ServicesProvider.selectionService) {
            ServicesProvider.selectionService.currentSlideIndex = index;
        }
        ServicesProvider.selectionService?.selectedSlides.add(...addedSlide);
    }

    public static adjustPosition = (sdkData) => {
        let offsetLeft = 0;
        let offsetTop = 0;
        const group = ServicesProvider.selectionService?.currentSlide;
        let occupied = false;
        const checkOccupied = (group): void => {
            group.children?.length && group.children.forEach((shape) => {
                if (shape.type === 'Group') {
                    checkOccupied(shape); // 递归查找groupShape
                }
                const shapeSdkModel = shape.copyModelHandler().getCopiedSdkModel();
                if (CPUtils.checkFrame(shapeSdkModel, sdkData, offsetLeft, offsetTop)) {
                    // 被占用
                    occupied = true;
                    offsetLeft += 100000;
                    offsetTop += 100000;
                    return false; // 存在重复的，结束跳出整个循环
                }
            });
        };
        checkOccupied(group);
        if (occupied) {
            if (sdkData.props.frame) {
                sdkData.props.frame.left += offsetLeft;
                sdkData.props.frame.top += offsetTop;
            } else {
                sdkData.props.frame = {
                    left: 0,
                    top: 0,
                };
            }
        }
    };

    public static checkFrame(shapeSdkModel, sdkData, offsetLeft: number, offsetTop: number): boolean {
        return shapeSdkModel.props.frame && sdkData.props.frame
            && shapeSdkModel.props.frame.left === sdkData.props.frame.left + offsetLeft
            && shapeSdkModel.props.frame.top === sdkData.props.frame.top + offsetTop
            && shapeSdkModel.props.frame.width === sdkData.props.frame.width
            && shapeSdkModel.props.frame.height === sdkData.props.frame.height;
    }

    public static checkPosition = (newElementSdkData: any[]) => {
        for (let i = 0; i < newElementSdkData.length; i++) {
            const currentSdkData = newElementSdkData[i];
            for (let j = 0; j < i; j++) {
                const tempSdkData = newElementSdkData[j];
                if (tempSdkData.props.frame && currentSdkData.props.frame
                    && tempSdkData.props.frame.left === currentSdkData.props.frame.left
                    && tempSdkData.props.frame.top === currentSdkData.props.frame.top
                    && tempSdkData.props.frame.width === currentSdkData.props.frame.width
                    && tempSdkData.props.frame.height === currentSdkData.props.frame.height) {
                    // 被占用
                    currentSdkData.props.frame.left += 100000;
                    currentSdkData.props.frame.top += 100000;
                }
            }
        }
    };

    /**
     *
     * 是否是mac safari
     * @static
     * @memberof CPUtils
     */
    public static isMacSafari(): boolean {
        return !!(!browser.isMobile && browser.isSafari);
    }

    /**
     *
     * 是否是旧版mac企微（旧版mac企微也是safari内核但是ua里没有体现）
     * 这个判断之后可以放在temp-config里统一管理
     * @static
     * @memberof CPUtils
     */
    public static isOldMacWxWork(): boolean {
        return !!(browser.isMac && browser.isWxWork && !browser.isCEFWxWork);
    }

    /**
     *
     * 从外部文档向内部文档拷贝slide时，需要对slide进行相应的处理：如缩放、layoutid、去除placeholder等逻辑
     * @static
     * @memberof CPUtils
     */
    public static adaptSlideSdkData = (slideSDKData, slideSize) => {
        // 处理layoutId
        const layout = SlideDataAdapter.getLayout();
        slideSDKData.layoutId = layout.id;

        SlideDataAdapter.checkPlaceHolder(slideSDKData);

        // 调整缩放
        if (slideSize) {
            // 传入的宽高
            const outWidth = slideSize.width;
            const outHeight = slideSize.height;
            // 当前的宽高
            const { currentHeight, currentWidth } = (() => {
                const { sldSz } = SlideDataServices.SDKDataServices.PresentationStoreService()!.getPresentation().props;
                return {
                    currentWidth: sldSz.cx,
                    currentHeight: sldSz.cy,
                };
            })();

            let ratio = 0; // 缩放比例
            let offsetTop = 0; // 居中处理的偏移量
            let offsetLeft = 0;
            const ratioH = currentHeight / outHeight;
            // 计算缩放比
            if ((currentWidth / outWidth) <= (currentHeight / outHeight)) {
                ratio = currentWidth / outWidth;
                const targetHeight = outHeight * ratio;
                offsetTop = (currentHeight - targetHeight) / 2;
            } else {
                ratio = currentHeight / outHeight;
                const targetWidth = outWidth * ratio;
                offsetLeft = (currentWidth - targetWidth) / 2;
            }

            if (_get(slideSDKData, 'slideData.groupShape.shapeList')) {
                slideSDKData.slideData.groupShape.shapeList.forEach((element) => {
                    // 处理通用缩放（frame）
                    if (_get(element, 'props.frame')) {
                        const { frame } = element.props;
                        element.props.frame.top = (frame.top * ratioH) + offsetTop;
                        element.props.frame.left = (frame.left * ratio) + offsetLeft;
                        element.props.frame.width = frame.width * ratio;
                        element.props.frame.height = frame.height * ratio;
                    }
                    SlideDataAdapter.adaptElement(element, ratio, ratioH);
                });
            }
        }
    };

    /**
     * stringify插件数据
     * @param {Object} sdkData 元素的原始数据
     * @return {String} JSON字符串化的插件数据 或 空字符串
     */
    public static stringifyAddonData = (sdkData) => {
        const { props = {} } = sdkData;
        const { addon = {} } = props;

        if (addon.type === 'latex') {
            // 公式图片插件数据
            // 复制公式图片
            // 1. 检查复制内容是否为公式图片
            // 2. 如是公式图片，则将公式图片的标识数据转换为JSON字符串，其中：aid - 插件ID，type - 插件类型，extraData - 插件数据
            const data = JSON.stringify(addon).replace(/"/g, '-');
            return `addon="${data}"`;
        }

        // 如不是相关插件元素，则返回空
        return '';
    };

    /**
     * parse插件数据
     * @param {String} addonData stringify插件数据
     * @return {Object} parse后的数据对象
     */
    static parseAddonData = (addonData: string) => JSON.parse(addonData.replace(/-/g, '"'));
}
